import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useState } from 'react'
import { FiEdit2, FiMoreVertical, FiPlus, FiTrash2 } from 'react-icons/fi'
import {
  MaintenanceFee,
  useDeleteMaintenanceFee,
  useMaintenanceFees,
} from '../../../../hooks/useMaintenanceFees'
import ConfirmationDialog from '../../../ui/ConfirmationDialog'
import { MaintenanceFeeForm } from '../MaintenanceFeeForm/MaintenanceFeeForm'

interface MaintenanceFeesListProps {
  propertyId: string
}

export function MaintenanceFeesList({ propertyId }: MaintenanceFeesListProps) {
  const {
    data: maintenanceFees,
    isLoading,
    error,
  } = useMaintenanceFees(propertyId)
  const deleteMaintenanceFee = useDeleteMaintenanceFee()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure()
  const [editingFee, setEditingFee] = useState<MaintenanceFee | null>(null)
  const [feeToDelete, setFeeToDelete] = useState<MaintenanceFee | null>(null)

  const handleEdit = (fee: MaintenanceFee) => {
    setEditingFee(fee)
    onOpen()
  }

  const handleDelete = (fee: MaintenanceFee) => {
    setFeeToDelete(fee)
    onDeleteDialogOpen()
  }

  const handleConfirmDelete = () => {
    if (feeToDelete) {
      deleteMaintenanceFee.mutate(feeToDelete.id)
    }
  }

  const handleCloseDeleteDialog = () => {
    onDeleteDialogClose()
    setFeeToDelete(null)
  }

  const handleCloseModal = () => {
    setEditingFee(null)
    onClose()
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return format(new Date(`${dateString}T03:00:00`), 'dd/MM/yyyy', {
      locale: ptBR,
    })
  }

  if (isLoading) {
    return (
      <Card>
        <CardBody>
          <VStack spacing={4}>
            <Spinner size="lg" />
            <Text>Carregando taxas de manutenção...</Text>
          </VStack>
        </CardBody>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardBody>
          <Alert status="error">
            <AlertIcon />
            <Box>
              <AlertTitle>Erro ao carregar taxas de manutenção!</AlertTitle>
              <AlertDescription>
                {error instanceof Error ? error.message : 'Erro desconhecido'}
              </AlertDescription>
            </Box>
          </Alert>
        </CardBody>
      </Card>
    )
  }

  const totalCost =
    maintenanceFees?.reduce((sum, fee) => sum + fee.cost, 0) || 0

  return (
    <>
      <Card>
        <CardHeader>
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Heading size="md">Taxas de Manutenção</Heading>
              <Text fontSize="sm" color="gray.600">
                Total: {formatCurrency(totalCost)} •{' '}
                {maintenanceFees?.length || 0} registros
              </Text>
            </VStack>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              size="sm"
              onClick={onOpen}
            >
              Adicionar Taxa
            </Button>
          </HStack>
        </CardHeader>

        <CardBody>
          {!maintenanceFees || maintenanceFees.length === 0 ? (
            <VStack spacing={4} py={8}>
              <Text color="gray.500" textAlign="center">
                Nenhuma taxa de manutenção registrada para esta propriedade.
              </Text>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                variant="outline"
                onClick={onOpen}
              >
                Adicionar Primeira Taxa
              </Button>
            </VStack>
          ) : (
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Data do Reparo</Th>
                    <Th>Custo</Th>
                    <Th>Descrição</Th>
                    <Th width="50px">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {maintenanceFees.map((fee) => (
                    <Tr key={fee.id}>
                      <Td>{formatDate(fee.repair_date)}</Td>
                      <Td fontWeight="semibold" color="red.600">
                        {formatCurrency(fee.cost)}
                      </Td>
                      <Td>
                        <Text noOfLines={2} maxW="300px">
                          {fee.description || 'Sem descrição'}
                        </Text>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                            aria-label="Ações"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<FiEdit2 />}
                              onClick={() => handleEdit(fee)}
                            >
                              Editar
                            </MenuItem>
                            <MenuItem
                              icon={<FiTrash2 />}
                              color="red.500"
                              onClick={() => handleDelete(fee)}
                              isDisabled={deleteMaintenanceFee.isPending}
                            >
                              {deleteMaintenanceFee.isPending
                                ? 'Excluindo...'
                                : 'Excluir'}
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </Card>

      <MaintenanceFeeForm
        isOpen={isOpen}
        onClose={handleCloseModal}
        propertyId={propertyId}
        editingFee={editingFee}
      />

      {/* Delete Confirmation Dialog */}
      {feeToDelete && (
        <ConfirmationDialog
          isOpen={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleConfirmDelete}
          title="Confirmar Exclusão"
          message={`Tem certeza que deseja excluir esta taxa de manutenção de ${formatCurrency(
            feeToDelete.cost
          )}?`}
          confirmText="Excluir"
          confirmColorScheme="red"
          isLoading={deleteMaintenanceFee.isPending}
        />
      )}
    </>
  )
}
